import { memo, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtom } from 'jotai';
import styled from 'styled-components';
import 'tippy.js/dist/tippy.css';
import './Map.css';
import MapDataLayerController from './controllers/MapDataLayerController';
import AllDisplayedDataBoundsController from './controllers/AllDisplayedDataBoundsController';
import StylingMapDataPanel from './panels/StylingMapDataPanel';
import UploadMapDataPanel from './panels/UploadMapDataPanel';
import DataTablePanel from './panels/DataTablePanel/DataTablePanel';
import GeocollectDatatablePanel from './panels/GeocollectDatatablePanel/GeocollectDatatablePanel';
import { showCommentPanelAtom, showSatelliteImagePanelAtom, showDataTableAtom, showElementDetailPanelAtom, showNavigatorPanelAtom, showGeocollectPanelAtom, showLegendPanelAtom, showForestWatchPanelAtom, showSearchAddressPanelAtom, showSharePanelAtom, showStylePopupAtom, showUploadTaskPanelAtom, showSamPanelAtom, showChatSamPanelAtom, showMinimapAtom, mapContextMenuAtom, showMapDataDetailPanelAtom } from './stores/mapStore';
import { showMapViewAtom } from '~/layouts/components/Header/components/MapView/store';
import { showBasemapPanelAtom } from '~/layouts/components/Header/components/BasemapSelector/BasemapSelectorStore';
import MapViewSidebarPanel from './panels/MapViewSidebarPanel/MapViewSidebarPanel';
import BasemapSidebarPanel from './panels/BasemapSidebarPanel/BasemapSidebarPanel';
import CommentPanel from './panels/CommentPanel/CommentPanel';
import SearchAddressPanel from './panels/SearchAddressPanel/SearchAddressPanel';
import SharePanel from './panels/SharePanel/SharePanel';
import BottomRightPanel from './panels/BottomRightPanel/BottomRightPanel';
import ProjectSettingPanel from './panels/ProjectSettingPanel/ProjectSettingPanel';
import { headerCenter_currentState_Atom, HeaderCenter_State } from '~/layouts/components/Header/HeaderCenter/store';
import LegendPanel from './panels/LegendPanel/LegendPanel';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import '@maplibre/maplibre-gl-compare/style.css'
import MapUtils from './utils/MapUtils';
import { MAPTILER_API_KEY } from '~/config/config';
import { TerraDraw } from 'terra-draw';
import ElementDetailPanel from './panels/ElementDetailPanel/ElementDetailPanel';
import UploadTaskPanel from './panels/UploadTaskPanel/UploadTaskPanel';
import CreateUploadTaskPanel from './panels/CreateUploadTaskPanel';
import { showCreateUploadTaskPanelAtom } from './panels/CreateUploadTaskPanel/store/CreateUploadTaskPanelStore';
import CommentMapController from './Comment/CommentMapController';
import ForestWatchPanel from './panels/ForestWatchPanel/ForestWatchPanel';
import NavigatorPanel from './panels/NavigatorPanel/NavigatorPanel';
import S2MosaicFinder from './panels/ForestWatchPanel/SatelliteImageTab/finders/S2MosaicFinder/S2MosaicFinder';
import S2MosaicMapController from './panels/ForestWatchPanel/SatelliteImageTab/finders/S2MosaicFinder/S2MosaicMapController';
import S2MosaicQuarterlyMapController from './panels/ForestWatchPanel/SatelliteImageTab/finders/S2MosaicQuarterlyFinder/S2MosaicQuarterlyMapController';
import LandsatMosaicMapController from './panels/ForestWatchPanel/SatelliteImageTab/finders/LandsatMosaicFinder/LandsatMosaicMapController';
import PlanetMapController from './panels/ForestWatchPanel/SatelliteImageTab/finders/PlanetFinder/PlanetMapController';
import LandsatMapController from './panels/ForestWatchPanel/SatelliteImageTab/finders/LandsatFinder/LandsatMapController';
import S2SingleMapController from './panels/ForestWatchPanel/SatelliteImageTab/finders/S2SingleFinder/S2SingleMapController';
import Compare from '@maplibre/maplibre-gl-compare'
import KeyboardController from './controllers/KeyboardController';
import SatelliteImagePanel from './panels/ForestWatchPanel/SatelliteImageTab/SatelliteImagePanel/SatelliteImagePanel';
import { SamPanel } from './panels/SAMPanel/SAMPanel';
import { ChatSamPanel } from './panels/ChatSAMPanel/ChatSAMPanel';
import { isMapLoadedDoneAtom } from './stores/mapStore';
import { InboxOutlined } from '@ant-design/icons';
import UploadTask_UploadPanel, { showPanel as showUploadTask_UploadPanel } from './panels/UploadTask_UploadPanel/UploadTask_UploadPanel';
import MapData_UpdatePanel from './panels/MapData_UpdatePanel/MapData_UpdatePanel';
import UploadTask_UpdatePanel from './panels/UploadTask_UpdatePanel/UploadTask_UpdatePanel';
import MapDataDetailPanel from './panels/MapDataDetailPanel';
import MemberMapController from './Member/MemberMapController';
import ElementMapController from './Element/ElementMapController';
import Minimap from './Minimap/Minimap';
import MapContextMenu from './MapContextMenu/MapContextMenu';
import { CommentMapUtilOptimized } from './Comment/CommentMapUtilOptimized';
import { MemberMapUtilOptimized } from './Member/MemberMapUtilOptimized';
import { ElementMapUtilOptimized } from './Element/ElementMapUtilOptimized';
import CollectItemsMapController from './controllers/CollectItemsMapController';
import CollectFormController from './controllers/CollectFormController';
import ProjectMemberController from './controllers/ProjectMemberController';
import ProjectDefaultViewController from './controllers/ProjectDefaultViewController';
import CollectItemPopup from './controllers/popups/CollectItemPopup';
import FreePhotoPopup from './controllers/popups/FreePhotoPopup';
import FreePhotoVectorTileController from './controllers/FreePhotoVectorTileController';
import CollectPhotoVectorTileController from './controllers/CollectPhotoVectorTileController';
import MapDataActionController from './controllers/MapDataActionController';
import UploadTaskProgressController from './controllers/UploadTaskProgressController';
import GeocollectItemPanel from './panels/GeocollectItemPanel/GeocollectItemPanel';
import UserProfile from './components/UserProfile/UserProfile';
// Declare a global type extension for 'window' to include 'map'
declare global {
  interface Window {
    beforeMap: maplibregl.Map | undefined;
    map: maplibregl.Map | undefined;
    draw: TerraDraw;
    compare: Compare | undefined;
  }
}

function MapScreen() {
  const { t } = useTranslation();
  const mapContainer = useRef<any>(null);
  const map2Container = useRef<any>(null);
  const mapComparisionContainer = useRef<any>(null);
  const map = useRef<any>(null);
  const [lng, setLng] = useState(20.83416);
  const [lat, setLat] = useState(10.027764);
  const [zoom, setZoom] = useState(2);

  const [openBottomDatatable, setBottomDatatableVisible] = useState(false);
  const [isMapLoadedDone, setMapLoadedDone] = useAtom(isMapLoadedDoneAtom);

  const [showStylePopup, setShowStylePopup] = useAtom(showStylePopupAtom);
  const [showDataTable, setShowDataTable] = useAtom(showDataTableAtom);
  const [showSearchAddressPanel, setShowSearchAddressPanel] = useAtom(showSearchAddressPanelAtom);

  useEffect(() => {
    (window as any).toggleDataTable = (show?: boolean) => {
      setShowDataTable((prev) => (show !== undefined ? show : !prev));
    };
  }, [setShowDataTable]);
  const [showCommentPanel, setShowCommentPanel] = useAtom<boolean>(showCommentPanelAtom);
  const [showSharePanel, setShowSharePanel] = useAtom<boolean>(showSharePanelAtom);
  const [showLegendPanel, setShowLegendPanel] = useAtom<boolean>(showLegendPanelAtom);
  const [headerCenter_CurrentState, setHeaderCenterCurrentProject] = useAtom(headerCenter_currentState_Atom);
  const [showElementDetailPanel, setShowElementDetailPanel] = useAtom(showElementDetailPanelAtom);
  const [showGeocollectPanel, setShowGeocollectPanel] = useAtom(showGeocollectPanelAtom);
  const [showUploadTaskPanel, setShowUploadTaskPanel] = useAtom(showUploadTaskPanelAtom);
  const [showCreateUploadTaskPanel, setShowCreateUploadTaskPanel] = useAtom(showCreateUploadTaskPanelAtom);
  const [showForestWatchPanel, setShowForestWatchPanel] = useAtom(showForestWatchPanelAtom);
  const [showNavigatorPanel, setShowNavigatorPanel] = useAtom(showNavigatorPanelAtom);
  const [showSatelliteImagePanel, setShowSatelliteImagePanel] = useAtom(showSatelliteImagePanelAtom);
  const [showSamPanel, setShowSamPanel] = useAtom(showSamPanelAtom);
  const [showChatSamPanel, setShowChatSamPanel] = useAtom(showChatSamPanelAtom);
  const [showMinimap, setShowMinimap] = useAtom(showMinimapAtom);
  const [mapContextMenu, setMapContextMenu] = useAtom(mapContextMenuAtom);
  const [showMapViewPanel, setShowMapViewPanel] = useAtom(showMapViewAtom);
  const [showBasemapPanel, setShowBasemapPanel] = useAtom(showBasemapPanelAtom);
  const [showMapDataDetailPanel, setShowMapDataDetailPanel] = useAtom(showMapDataDetailPanelAtom);

  const [isDraggingOverMap, setIsDraggingOverMap] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDraggingOverMap) {
      setIsDraggingOverMap(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setIsDraggingOverMap(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOverMap(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);
      showUploadTask_UploadPanel(files);
    }
  };

  useEffect(() => {
    if (map.current) return;
    setMapLoadedDone(false);

    // Đọc tọa độ từ URL query parameters nếu có (lat, lon/lng, zoom)
    const searchParams = new URLSearchParams(window.location.search);
    const urlLat = searchParams.get('lat');
    const urlLon = searchParams.get('lon') || searchParams.get('lng');
    const urlZoom = searchParams.get('zoom');

    const parsedLat = urlLat ? parseFloat(urlLat) : NaN;
    const parsedLon = urlLon ? parseFloat(urlLon) : NaN;
    const parsedZoom = urlZoom ? parseFloat(urlZoom) : NaN;

    const initialLng = !isNaN(parsedLon) ? parsedLon : lng;
    const initialLat = !isNaN(parsedLat) ? parsedLat : lat;
    const initialZoom = !isNaN(parsedZoom) ? parsedZoom : zoom;

    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: `https://api.maptiler.com/maps/streets-v2/style.json?key=${MAPTILER_API_KEY}`,
      center: [initialLng, initialLat],
      zoom: initialZoom,
      // projection: {
      //   name: 'globe',
      // },
      maxZoom: 22,
      localIdeographFontFamily: 'Apple Color Emoji, Noto Color Emoji, Segoe UI Emoji',
      transformRequest: ((url: string, resourceType: any) => {
        const jwt = localStorage.getItem("jwt");
        if (
          resourceType === 'Tile' &&
          (url.startsWith('https://strapi.admin.tlgeo.xyz') ||
           url.includes('/remote-image-alert-ext/') ||
           url.includes('/s2-mosaic') ||
           url.includes('/satellite/planet/')) &&
          jwt
        ) {
          return {
            url: url,
            headers: { 'Authorization': 'Bearer ' + jwt }
          }
        }
        return { url };
      }) as maplibregl.RequestTransformFunction,
      attributionControl: false,
    });
    window.map = map.current;

    let urlUpdateTimer: any = null;
    const updateUrlParams = () => {
      if (!map.current) return;
      const center = map.current.getCenter();
      const currentZoom = map.current.getZoom();

      const newLat = center.lat.toFixed(5);
      const newLon = center.lng.toFixed(5);
      const newZoom = currentZoom.toFixed(2);

      const url = new URL(window.location.href);
      if (
        url.searchParams.get('lat') !== newLat ||
        url.searchParams.get('lon') !== newLon ||
        url.searchParams.get('zoom') !== newZoom
      ) {
        url.searchParams.set('lat', newLat);
        url.searchParams.set('lon', newLon);
        url.searchParams.set('zoom', newZoom);
        window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
      }
    };

    map.current.on('move', () => {
      setLng(map.current.getCenter().lng.toFixed(4));
      setLat(map.current.getCenter().lat.toFixed(4));
      setZoom(map.current.getZoom().toFixed(2));

      if (urlUpdateTimer) clearTimeout(urlUpdateTimer);
      urlUpdateTimer = setTimeout(() => {
        updateUrlParams();
      }, 300);
    });

    map.current.on('moveend', () => {
      if (urlUpdateTimer) clearTimeout(urlUpdateTimer);
      updateUrlParams();
    });
    map.current.on('style.load', () => {

      // map.current.setFog({
      //   color: 'rgb(186, 210, 235)', // Lower atmosphere
      //   'high-color': 'rgb(36, 92, 223)', // Upper atmosphere
      //   'horizon-blend': 0.02, // Atmosphere thickness (default 0.2 at low zooms)
      //   'space-color': 'rgb(11, 11, 25)', // Background color
      //   'star-intensity': 0.6, // Background star brightness (default 0.35 at low zoooms )
      // });

      // setup basemap if needed
      if (false) {
        const googleLayerUrl = localStorage.getItem('googleLayerUrl');
        if (googleLayerUrl) {
          MapUtils.changeBasemap(googleLayerUrl);
        }
      }

      {
        // window.map?.setConfigProperty('basemap', 'lightPreset', 'dawn');
        // const zoomBasedReveal = (value) => {
        //   return [
        //     'interpolate',
        //     ['linear'],
        //     ['zoom'],
        //     11,
        //     0.0,
        //     13,
        //     value
        //   ];
        // };
        // window.map?.setRain({
        //   density: zoomBasedReveal(0.5),
        //   intensity: 1.0,
        //   color: '#a8adbc',
        //   opacity: 0.7,
        //   vignette: zoomBasedReveal(1.0),
        //   'vignette-color': '#464646',
        //   direction: [0, 80],
        //   'droplet-size': [2.6, 18.2],
        //   'distortion-strength': 0.7,
        //   'center-thinning': 0 // Rain to be displayed on the whole screen area
        // });
      }

      // Set map as loaded and force re-render controllers
      setMapLoadedDone(true);
    });

    // map.current.on('styledata', (e) => {
    //   console.log('Style đã được thay đổi hoặc cập nhật.', e);
    //   // Set map as loaded and force re-render controllers
    //   setMapLoadedDone(false);
    //   setTimeout(() => {
    //     setMapLoadedDone(true);
    //   }, 100);
    // });

    // Add right-click context menu handler
    map.current.on('contextmenu', (e) => {
      e.preventDefault();
      const { lng, lat } = e.lngLat;
      const { x, y } = e.point;

      setMapContextMenu({
        x: x,
        y: y,
        lng: lng,
        lat: lat
      });
    });

    // Close context menu on left click
    map.current.on('click', () => {
      if (mapContextMenu) {
        setMapContextMenu(null);
      }
    });

    {
      window.beforeMap = new maplibregl.Map({
        container: map2Container.current,
        style:
          `https://api.maptiler.com/maps/streets-v2/style.json?key=${MAPTILER_API_KEY}`,
        center: [lng, lat],
        zoom: zoom,
        attributionControl: false,
      });
      window.compare = new Compare(window.beforeMap, window.map, mapComparisionContainer.current, {
        // mousemove: true, // Optional. Set to true to enable swiping during cursor movement.
        orientation: 'vertical' // Optional. Sets the orientation of swiper to horizontal or vertical, defaults to vertical
      });
      window.compare.setSlider(0);
    }

    // Listen for basemap changes (for google basemap type)
    const handleBasemapChanged = () => {
      BasemapMapUtil.clearAllMapCaches();
      setMapLoadedDone(false);
      const fn = (e: any) => {
        // Only trigger when style is fully loaded (not just sources)
        if (!e.sourceDataType) {
          console.log('style fully loaded on handleBasemapChanged', e);
          setMapLoadedDone(true);
          map.current.off('styledata', fn);
        }
      }
      map.current.on('styledata', fn);

    };

    window.addEventListener('basemapChanged', handleBasemapChanged);

    // Automatically resize map whenever container dimensions change in the DOM
    let resizeRafId: number | null = null;
    const containerToObserve = mapContainer.current;
    let resizeObserver: ResizeObserver | null = null;

    if (containerToObserve) {
      resizeObserver = new ResizeObserver(() => {
        if (resizeRafId) cancelAnimationFrame(resizeRafId);
        resizeRafId = requestAnimationFrame(() => {
          if (map.current) {
            map.current.resize();
          }
          if (window.beforeMap) {
            window.beforeMap.resize();
          }
        });
      });
      resizeObserver.observe(containerToObserve);
    }

    // Cleanup
    return () => {
      window.removeEventListener('basemapChanged', handleBasemapChanged);
      if (resizeRafId) cancelAnimationFrame(resizeRafId);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, []);

  return (
    <>
      <UI_Wrapper>
        <UI_MapWrapper
          ref={mapComparisionContainer}
          style={{ flex: 1 }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <UI_Map2Container ref={map2Container} />
          <UI_MapContainer ref={mapContainer} />
          {isDraggingOverMap && (
            <UI_DragOverlay>
              <div className="dropzone-box">
                <InboxOutlined style={{ fontSize: 52, color: '#1677ff' }} />
                <h3>Thả tệp GIS vào đây để mở dữ liệu</h3>
                <p>Hỗ trợ MicroStation DGN (.dgn), Shapefile (ZIP), GeoJSON, KML, GPX, GeoTIFF...</p>
              </div>
            </UI_DragOverlay>
          )}
          <BottomRightPanel />
        </UI_MapWrapper>

        <UploadMapDataPanel />
        <UploadTask_UploadPanel />
        <MapData_UpdatePanel />
        <UploadTask_UpdatePanel />

        {
          showLegendPanel && <LegendPanel />
        }

        {
          showStylePopup && <StylingMapDataPanel />
        }

        {
          showMapDataDetailPanel && <MapDataDetailPanel />
        }

        {
          showDataTable && <DataTablePanel />
        }

        {
          showSearchAddressPanel && <SearchAddressPanel />
        }

        {
          showCommentPanel && <CommentPanel />
        }

        {
          showMapViewPanel && <MapViewSidebarPanel />
        }

        {
          showBasemapPanel && <BasemapSidebarPanel />
        }

        {
          showSharePanel && <SharePanel />
        }

        {
          showElementDetailPanel && <ElementDetailPanel />
        }

        {
          showGeocollectPanel && <GeocollectDatatablePanel />
        }

        {
          showUploadTaskPanel && <UploadTaskPanel />
        }

        {
          showCreateUploadTaskPanel && <CreateUploadTaskPanel />
        }

        {
          showForestWatchPanel && <ForestWatchPanel />
        }

        {
          showNavigatorPanel && <NavigatorPanel />
        }

        {
          showSatelliteImagePanel && <SatelliteImagePanel />
        }

        {
          showSamPanel && <SamPanel />
        }

        {
          showChatSamPanel && <ChatSamPanel />
        }

        {headerCenter_CurrentState == HeaderCenter_State.SETTINGS && <ProjectSettingPanel />}

      </UI_Wrapper>

      {showMinimap && <Minimap />}
      <MapContextMenu />
      <UserProfile />
      <MapDataLayerController />
      <MapDataActionController />
      <UploadTaskProgressController />
      <CommentMapController />
      <MemberMapController />
      <ElementMapController />
      <CollectItemsMapController />
      <CollectFormController />
      <ProjectMemberController />
      <ProjectDefaultViewController />
      <FreePhotoVectorTileController />
      <CollectPhotoVectorTileController />
      <AllDisplayedDataBoundsController />
      <S2MosaicMapController />
      <S2MosaicQuarterlyMapController />
      <LandsatMosaicMapController />
      <PlanetMapController />
      <LandsatMapController />
      <S2SingleMapController />
      <KeyboardController />
      <CollectItemPopup />
      <FreePhotoPopup />
      <GeocollectItemPanel />
    </>
  );
}

export default memo(MapScreen);

const UI_Wrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`
const UI_MapWrapper = styled.div`
  position: relative;
`
const UI_MapContainer = styled.div`
  max-height: 100%;
  min-height: 100%;
  position: absolute;
  top: 0;
  bottom: 0;
  width: 100%;
  // z-index: 9999;
`

const UI_Map2Container = styled.div`
  max-height: 100%;
  min-height: 100%;
  position: absolute;
  top: 0;
  bottom: 0;
  width: 100%;

  // z-index: 8999;
`;

const UI_DragOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(4px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;

  .dropzone-box {
    padding: 32px 48px;
    background: #ffffff;
    border: 2px dashed #1677ff;
    border-radius: 12px;
    text-align: center;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);

    h3 {
      margin-top: 12px;
      margin-bottom: 4px;
      font-size: 18px;
      color: #1f1f1f;
    }

    p {
      margin: 0;
      color: #8c8c8c;
      font-size: 13px;
    }
  }
`;